<template>
  <div class="business-elements-editor">
    <div class="element-container element-header-container" :class="{ 'text-deactivated' : !isEditable}">
      <div class="element"><p>Description</p></div>
      <div class="durationQuantity"><p>Temps/QTÉ</p></div>
      <div class="unitPrice"><p>Prix unitaire</p></div>
      <div class="discountedRate"><p>Remise (%)</p></div>
      <div class="tva"><p>TVA (%)</p></div>
      <div class="amount"><p>Prix HT</p></div>
    </div>

    <div class="elements-container" ref="elementsContainer">
      <BusinessElementRow
          v-for="(el, index) in selfElements"
          :key="elementKey(el)"
          :element="el"
          :is-editable="isEditable"
          :is-drag-over="dragOverIndex === index"
          @dragstart="onDragStart($event, index)"
          @dragover="onDragOver($event, index)"
          @drop="onDrop($event, index)"
          @dragend="onDragEnd"
          @remove="removeElement(index)"
      />
    </div>

    <div v-if="isEditable" class="plus-button-container">
      <button type="button" @click="isChoiceDisplayed = !isChoiceDisplayed" class="add-element button-icon">
        <span class="fas fa-plus"></span>

        <span class="choice" :class="{ 'choice-displayed': isChoiceDisplayed }">
          <button type="button" @click="addElement('element')">Elément</button>
          <button type="button" @click="addElement('title')" style="margin-top: 5px">Titre</button>
        </span>
      </button>
    </div>

    <div style="height: 20px"></div>

    <div class="total-amount" :class="{ 'text-deactivated': !isEditable }">
      <p class="text">HT</p>
      <div><p class="amount">{{ roundDownTo2Decimals(selfTotals.htAmount) }}</p>
        <p class="currency">€</p></div>
    </div>
    <div class="total-amount" :class="{ 'text-deactivated': !isEditable }">
      <p class="text">TVA</p>
      <div><p class="amount">{{ roundDownTo2Decimals(selfTotals.tvaAmount) }}</p>
        <p class="currency">€</p></div>
    </div>
    <div class="total-amount" :class="{ 'text-deactivated': !isEditable }">
      <p class="text">TTC</p>
      <div><p class="amount">{{ roundDownTo2Decimals(selfTotals.ttcAmount) }}</p>
        <p class="currency">€</p></div>
    </div>
  </div>
</template>

<script>
import BusinessElementRow from "./BusinessElementRow.vue";
import {roundDownTo2Decimals} from "@/utils";

export default {
  name: 'BusinessElementsEdition',
  components: {BusinessElementRow},
  props: {
    elements: {type: Array, required: true},
    isEditable: {type: Boolean, default: false},
    defaultUnitPrice: {type: [Number, String, undefined], default: undefined},
    defaultTvaPercent: {type: [Number, String, undefined], default: undefined},
    totals: {type: Object, required: true},
  },
  data() {
    return {
      dragStartIndex: null,
      dragOverIndex: null,
      isChoiceDisplayed: false,
      selfTotals: this.totals,
      selfElements: this.elements,
    };
  },
  watch: {
    selfElements: {
      deep: true,
      immediate: true,
      handler(elements) {
        for (const i in elements) {
          const discount = elements[i].discountedRate.value
              ? (100 - elements[i].discountedRate.value) / 100
              : 1;
          const amount = elements[i].durationQuantity.value * elements[i].unitPrice.value * discount;
          elements[i].amount.value = amount ? roundDownTo2Decimals(amount) : 0;
        }

        this.selfTotals.htAmount = elements.reduce((total, el) => total + (Number(el.amount.value) || 0), 0);
        this.selfTotals.tvaAmount = elements.reduce((total, el) => {
          return total + (Number(el.tva.value) && Number(el.amount.value)
              ? (Number(el.tva.value) / 100) * Number(el.amount.value)
              : 0);
        }, 0);
        this.selfTotals.ttcAmount = this.selfTotals.htAmount + this.selfTotals.tvaAmount;
      }
    }
  },
  created() {
    // Clé propre à chaque ligne (et non son index) : les champs d'une ligne gardent une copie de leur contenu (selfContent),
    // si Vue réutilisait le composant d'une autre ligne après un déplacement, il afficherait et modifierait l'ancienne ligne.
    this.elementKeys = new WeakMap();
    this.nextElementKey = 0;
  },
  methods: {
    roundDownTo2Decimals,
    elementKey(element) {
      if (!this.elementKeys.has(element)) {
        this.elementKeys.set(element, this.nextElementKey++);
      }
      return this.elementKeys.get(element);
    },
    addElement(type) {
      this.selfElements.push({
        type,
        text: {value: '', type: 'text'},
        durationQuantity: {value: '', type: 'number', step: 0.25},
        unitPrice: {value: this.defaultUnitPrice, type: 'number', step: 0.25},
        discountedRate: {value: '', type: 'number'},
        tva: {value: this.defaultTvaPercent, type: 'number'},
        amount: {value: '', type: 'number', readOnly: true},
      });
    },
    removeElement(index) {
      this.selfElements.splice(index, 1);
    },
    onDragStart(event, index) {
      this.dragStartIndex = index;
      event.dataTransfer.effectAllowed = 'move';
    },
    onDragOver(event, index) {
      this.dragOverIndex = index;
      event.dataTransfer.dropEffect = 'move';
    },
    onDrop(event, index) {
      const dragStartIndex = this.dragStartIndex;
      this.dragStartIndex = null;
      this.dragOverIndex = null;
      if (dragStartIndex !== null && dragStartIndex !== index) {
        const [moved] = this.selfElements.splice(dragStartIndex, 1);
        this.selfElements.splice(index, 0, moved);
      }
    },
    onDragEnd() {
      this.dragStartIndex = null;
      this.dragOverIndex = null;
    }
  }
};
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.elements-container
  max-height 300px
  overflow-y auto

.elements-container .element-pre-container
  width 100%

.element-header-container
  margin-bottom 1em
  padding-right 38px

  div
    p
      margin 0
      font-weight bold
      font-size 14px

  &.text-deactivated
    div p
      color #717171

.element-container
  display flex
  gap 2px

  .title
    width 100%

    :deep(input.input)
      font-weight 600 !important

  .element
    width 100%

  .durationQuantity
    width 200px

  .unitPrice
    width 270px

  .discountedRate
    width 200px

  .tva
    width 150px

  .amount
    display flex
    align-items center
    justify-content space-between
    width 150px

    p
      font-size 0.9em
      margin 0

      &.text-deactivated
        color #717171

      &.currency
        padding-right 5px

  .minus-button-container
    display flex
    align-items center

    button
      width 20px
      height 20px
      margin-right 5px

      svg
        margin-left -4px

.total-amount
  padding-right 45px
  width 130px
  display flex
  margin-left auto
  justify-content space-between

  &.text-deactivated
    padding-right 20px

    p
      color #717171

  p
    margin 0
    margin-bottom 5px

  div
    width 85px
    display flex
    justify-content space-between

  .text
    font-weight bold

.elements-container
  max-height 300px
  overflow-y auto

.element-header-container
  margin-bottom 1em
  padding-right 38px

  div p
    margin 0
    font-weight bold
    font-size 14px

  &.text-deactivated
    div p
      color #717171

.total-amount
  padding-right 45px
  width 130px
  display flex
  margin-left auto
  justify-content space-between

  &.text-deactivated
    padding-right 20px

    p
      color #717171

  p
    margin 0
    margin-bottom 5px

  div
    width 85px
    display flex
    justify-content space-between

  .text
    font-weight bold

.plus-button-container
  display flex
  justify-content flex-end
  margin-top 10px
  margin-bottom 10px

  .choice
    display none
    flex-direction column
    position absolute
    z-index 1
    margin-top -36px
    margin-left 25px
    padding 5px
    border-radius 6px
    background-color #b9cacf6e

    button
      border none
      cursor pointer
      border-radius 3px
      color THEME_COLOR_PRIMARY_OPPOSITE
      background-color THEME_COLOR_PRIMARY

  .add-element
    margin-top 15px
    margin-left auto
    width 30px
    height 30px

.choice-displayed
  display flex !important

.business-elements-editor
  padding-bottom 15px

  .element-header-container
    padding 8px 10px
    margin-bottom 4px
    border-bottom 2px solid #b4bfce

    div p
      font-size 11px
      font-weight 700
      letter-spacing 0.03em
      text-transform uppercase
      color #6b7076

    &.text-deactivated
      div p
        color #b4b8bd

  .elements-container
    max-height none !important
    overflow visible !important

  .element-pre-container
    border-bottom 1px solid #eef0f2

    &:hover
      background-color #fafbfc

  .element-container
    padding 6px 10px

    .amount p
      font-weight 700
      color #24272b

      &.text-deactivated
        color #b4b8bd

  .element-container :deep(input.input)
    padding 0
    text-indent 0
    height 25px
    border 2px solid rgba(255, 255, 255, 0)
    font-weight 400 !important

    &:hover
      border 2px solid THEME_COLOR_PRIMARY

  .element-pre-container.drag-over
    border-top 2px dashed THEME_COLOR_PRIMARY

  .plus-button-container
    margin-top 6px

    .choice
      background-color #f5f6f7

  .total-amount
    padding-right 10px

    .text
      color #6b7076

    .amount
      font-size 15px
      font-weight 700
      color #24272b

    &.text-deactivated
      .amount
        color #b4b8bd
</style>